import * as React from 'react';
import { Check, Plus, ShoppingBag, Trash2, X } from 'lucide-react';
import {
  useGalpoesMeliAdmin,
  useRemoverGalpaoMeli,
  useSalvarGalpaoMeli,
  type GalpaoMeliAdmin,
} from '@/hooks/admin';
import { useContexto } from '@/hooks/useContexto';
import { cn } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import {
  CabecalhoCartao,
  Campo,
  Cartao,
  CorpoCartao,
  Esqueleto,
  Pilula,
  TituloCartao,
} from '@/componentes/ui/primitivos';

/**
 * De qual galpão sai cada conta do Mercado Livre.
 *
 * O painel de Expedição ML precisa saber, para cada EMPRESA do Procfit, qual
 * conta (StoreId) e qual depósito do ML respondem por ela — e esse de-para não
 * existe em lugar nenhum: o hub tem StoreId e WarehouseStoreID, o Procfit tem
 * EMPRESA, e ninguém guarda a ligação.
 *
 * Isto já foi uma constante no código, e estava errado pelo mesmo motivo das
 * medidas de porta-palete: quem sabe qual galpão atende qual conta é a
 * operação, e conta nova não pode depender de deploy para aparecer.
 *
 * O NÓ DE ORIGEM não é decoração. Um pedido que o ML tem e que ainda não
 * chegou ao hub não tem StoreId nem warehouse do nosso lado — o único
 * identificador é o `origin.node.node_id` do envio. É ele que permite dizer
 * "este pedido perdido é do CD 2201" em vez de deixá-lo fora de todo painel.
 *
 * EMPRESAS SOMADAS existe porque um CD pode atender mais de uma empresa. O
 * painel do Mercado Livre agrupa pelo nó — o galpão físico —, e a LUFT (22) e
 * a TCL SEMP (23) despacham do mesmo BRP11687260422. Enquanto o painel somava
 * só a própria empresa, o CD 2201 mostrava 12 etiquetas a imprimir contra 47
 * no ML; as outras 19 eram da TCL, na mesma fila do mesmo galpão.
 */
export function MeliGalpoes({ habilitado = true }: { habilitado?: boolean }): JSX.Element {
  const consulta = useGalpoesMeliAdmin(habilitado);
  const salvar = useSalvarGalpaoMeli();
  const remover = useRemoverGalpaoMeli();
  const { empresas } = useContexto();
  const [editando, definirEditando] = React.useState<number | 'novo' | null>(null);

  const galpoes = consulta.data?.galpoes ?? [];
  const nomeDaEmpresa = React.useMemo(
    () => new Map(empresas.map((e) => [e.empresa, e.nome])),
    [empresas],
  );

  return (
    <Cartao>
      <CabecalhoCartao>
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-primaria-suave text-primaria">
            <ShoppingBag className="h-4 w-4" />
          </span>
          <div>
            <TituloCartao>Mercado Livre: conta e depósito por empresa</TituloCartao>
            <p className="text-[11.5px] text-textoFraco">
              É o que o painel de Expedição ML usa para saber de quem é cada pedido.
            </p>
          </div>
        </div>
        <Botao
          variante="secundaria"
          tamanho="pequeno"
          onClick={() => {
            salvar.reset();
            definirEditando('novo');
          }}
        >
          <Plus className="h-4 w-4" />
          Nova associação
        </Botao>
      </CabecalhoCartao>

      {consulta.isLoading ? (
        <CorpoCartao className="space-y-2">
          {Array.from({ length: 3 }, (_, i) => (
            <Esqueleto key={i} className="h-16" />
          ))}
        </CorpoCartao>
      ) : consulta.isError ? (
        <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      ) : (
        <ul className="divide-y divide-borda">
          {editando === 'novo' ? (
            <li className="bg-primaria-suave/40 p-4">
              <Formulario
                salvando={salvar.isPending}
                erro={salvar.error}
                aoCancelar={() => definirEditando(null)}
                aoSalvar={(g) => salvar.mutate(g, { onSuccess: () => definirEditando(null) })}
              />
            </li>
          ) : null}

          {galpoes.length === 0 && editando !== 'novo' ? (
            <li>
              <EstadoVazio
                titulo="Nenhuma associação cadastrada"
                detalhe="Sem isto, o painel de Expedição ML não mostra empresa nenhuma."
              />
            </li>
          ) : null}

          {galpoes.map((g) =>
            editando === g.empresa ? (
              <li key={g.empresa} className="bg-primaria-suave/40 p-4">
                <Formulario
                  inicial={g}
                  salvando={salvar.isPending}
                  erro={salvar.error}
                  aoCancelar={() => definirEditando(null)}
                  aoSalvar={(novo) =>
                    salvar.mutate(novo, { onSuccess: () => definirEditando(null) })
                  }
                />
              </li>
            ) : (
              <li key={g.empresa} className="flex flex-wrap items-center gap-3 px-4 py-3.5">
                <span className="tabular grid h-10 w-14 shrink-0 place-items-center rounded-lg gradiente-marca text-[13px] font-bold text-white">
                  {g.empresa}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-semibold">
                    {g.nome ?? `Empresa ${g.empresa}`}
                    {nomeDaEmpresa.get(g.empresa) ? (
                      <span className="font-normal text-textoFraco">
                        {' '}
                        · {nomeDaEmpresa.get(g.empresa)}
                      </span>
                    ) : null}
                  </p>
                  <p className="tabular text-[11.5px] text-textoFraco">
                    loja {g.loja}
                    {g.warehouse ? ` · depósito ${g.warehouse}` : ' · conta inteira'}
                    {g.node ? ` · nó ${g.node}` : ''}
                    {g.empresas && g.empresas.length > 1
                      ? ` · soma ${g.empresas.join(' + ')}`
                      : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {g.empresas && g.empresas.length > 1 ? (
                    <Pilula tom="alerta">{g.empresas.length} empresas</Pilula>
                  ) : null}
                  {g.warehouse ? <Pilula tom="neutro">depósito</Pilula> : <Pilula tom="ok">conta</Pilula>}
                  <Botao
                    variante="secundaria"
                    tamanho="pequeno"
                    onClick={() => {
                      salvar.reset();
                      definirEditando(g.empresa);
                    }}
                  >
                    Editar
                  </Botao>
                  <Botao
                    variante="fantasma"
                    tamanho="iconePequeno"
                    aria-label={`Remover a empresa ${g.empresa} do painel`}
                    title="Tira a empresa do painel de Expedição ML"
                    carregando={remover.isPending}
                    onClick={() => remover.mutate(g.empresa)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Botao>
                </div>
              </li>
            ),
          )}
        </ul>
      )}
    </Cartao>
  );
}

function Formulario({
  inicial,
  salvando,
  erro,
  aoSalvar,
  aoCancelar,
}: {
  inicial?: GalpaoMeliAdmin;
  salvando: boolean;
  erro: unknown;
  aoSalvar: (g: GalpaoMeliAdmin) => void;
  aoCancelar: () => void;
}): JSX.Element {
  const [empresa, definirEmpresa] = React.useState(String(inicial?.empresa ?? ''));
  const [loja, definirLoja] = React.useState(String(inicial?.loja ?? ''));
  const [warehouse, definirWarehouse] = React.useState(inicial?.warehouse ?? '');
  const [node, definirNode] = React.useState(inicial?.node ?? '');
  const [nome, definirNome] = React.useState(inicial?.nome ?? '');
  /*
    Texto livre, e não seletor múltiplo: são duas ou três empresas num cadastro
    que se mexe uma vez por ano, e o número é como a operação já as chama.
  */
  const [somadas, definirSomadas] = React.useState(
    (inicial?.empresas ?? []).filter((e) => e !== inicial?.empresa).join(', '),
  );

  const valido = Number(empresa) > 0 && Number(loja) > 0;

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valido) return;
        aoSalvar({
          empresa: Number(empresa),
          /* A própria empresa entra sempre — o servidor repõe de qualquer
             jeito, e mandá-la aqui deixa a lista igual ao que a tela mostra. */
          empresas: [
            ...new Set([
              Number(empresa),
              ...somadas
                .split(/[,;\s]+/)
                .map((n) => Number(n.trim()))
                .filter((n) => Number.isInteger(n) && n > 0),
            ]),
          ].sort((a, b) => a - b),
          loja: Number(loja),
          warehouse: warehouse.trim() || null,
          node: node.trim() || null,
          nome: nome.trim() || null,
        });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        <Rotulado rotulo="Empresa (Procfit)" dica="EMPRESA da televenda">
          <Campo
            inputMode="numeric"
            value={empresa}
            /* A empresa é a CHAVE: trocá-la num registro existente criaria
               outro em vez de editar este. */
            disabled={inicial !== undefined}
            onChange={(e) => definirEmpresa(e.currentTarget.value.replace(/\D/g, ''))}
            placeholder="22"
          />
        </Rotulado>
        <Rotulado rotulo="Loja (StoreId)" dica="conta no hub">
          <Campo
            inputMode="numeric"
            value={loja}
            onChange={(e) => definirLoja(e.currentTarget.value.replace(/\D/g, ''))}
            placeholder="13"
          />
        </Rotulado>
        <Rotulado rotulo="Depósito (warehouse)" dica="vazio = conta inteira">
          <Campo
            value={warehouse}
            onChange={(e) => definirWarehouse(e.currentTarget.value)}
            placeholder="83678510"
          />
        </Rotulado>
        <Rotulado rotulo="Nó de origem (ML)" dica="identifica pedido sem televenda">
          <Campo
            value={node}
            onChange={(e) => definirNode(e.currentTarget.value)}
            placeholder="BRP11687260422"
          />
        </Rotulado>
        <Rotulado rotulo="Empresas somadas" dica="outras do mesmo CD; vazio = só esta">
          <Campo
            value={somadas}
            onChange={(e) => definirSomadas(e.currentTarget.value.replace(/[^\d,;\s]/g, ''))}
            placeholder="23"
          />
        </Rotulado>
        <Rotulado rotulo="Nome na tela" dica="como a operação chama">
          <Campo
            value={nome}
            onChange={(e) => definirNome(e.currentTarget.value)}
            placeholder="Luft MG - CD 2201"
          />
        </Rotulado>
      </div>

      {erro ? (
        <p className="text-[12px] text-status-divergencia">
          {erro instanceof Error ? erro.message : 'Não foi possível salvar.'}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <Botao tamanho="pequeno" type="submit" carregando={salvando} disabled={!valido}>
          <Check className="h-4 w-4" />
          Salvar
        </Botao>
        <Botao variante="secundaria" tamanho="pequeno" type="button" onClick={aoCancelar}>
          <X className="h-4 w-4" />
          Cancelar
        </Botao>
      </div>
    </form>
  );
}

function Rotulado({
  rotulo,
  dica,
  children,
}: {
  rotulo: string;
  dica: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className={cn('block space-y-1')}>
      <span className="block text-[11px] font-semibold uppercase tracking-wide text-textoFraco">
        {rotulo}
      </span>
      {children}
      <span className="block text-[10.5px] text-textoSuave">{dica}</span>
    </label>
  );
}
